import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ClipboardList, ImagePlus, Send, Search } from "lucide-react";

const UTENSIL_UOMS = ["pcs", "pkt", "bottle", "set", "pair", "box", "roll"];

/** Downscale an image file to a small base64 data URL (max 400px). */
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 400;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.75));
    };
    img.onerror = reject;
    img.src = url;
  });
}

export default function UtensilsPage() {
  const [utensils, setUtensils] = useState([]);
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);   // utensil being edited or {} for new
  const [selected, setSelected] = useState({});   // {utensil_id: qty}
  const [assignOpen, setAssignOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/utensils").then((r) => setUtensils(r.data || [])).catch(() => {});
    api.get("/orders").then((r) => setOrders((r.data || []).filter((o) => !["completed", "cancelled"].includes(o.status)))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const ql = q.trim().toLowerCase();
  const visible = ql ? utensils.filter((u) => (u.name || "").toLowerCase().includes(ql)) : utensils;
  const selCount = Object.keys(selected).length;

  const toggleSel = (u) => {
    setSelected((s) => {
      const n = { ...s };
      if (n[u.id] !== undefined) delete n[u.id];
      else n[u.id] = u.quantity || 1;
      return n;
    });
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete "${u.name}" from the utensil library?`)) return;
    try {
      await api.delete(`/utensils/${u.id}`);
      toast.success("Utensil deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="utensils-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><ClipboardList className="w-3 h-3" /> Equipment Library</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Catering Utensils</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selCount > 0 && (
            <Button onClick={() => setAssignOpen(true)} data-testid="assign-utensils-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm">
              <Send className="w-4 h-4 mr-2" /> Assign {selCount} to order…
            </Button>
          )}
          <Button onClick={() => setEditing({})} data-testid="new-utensil-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> New Utensil
          </Button>
        </div>
      </div>

      <div className="relative max-w-md mb-6">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search utensils…" data-testid="utensil-search" className="pl-9 bg-[#050505] border-[#2a2a2a]" />
      </div>

      <p className="text-xs text-zinc-500 mb-4">Tick utensils below, then "Assign to order" — they'll appear on that order's checklist for the dispatch team to check off.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {visible.map((u) => {
          const isSel = selected[u.id] !== undefined;
          return (
            <div key={u.id} onClick={() => toggleSel(u)} data-testid={`utensil-card-${u.id}`}
              className={`card-lux p-4 cursor-pointer transition-all relative ${isSel ? "ring-1 ring-emerald-500/70" : "hover:-translate-y-0.5"}`}>
              <input type="checkbox" readOnly checked={isSel} data-testid={`utensil-select-${u.id}`} className="absolute top-3 right-3 w-4 h-4 accent-emerald-500 pointer-events-none" />
              <div className="w-full h-32 rounded-sm bg-[#101010] border border-[#1a1a1a] flex items-center justify-center overflow-hidden mb-3">
                {u.image_data_url
                  ? <img src={u.image_data_url} alt={u.name} className="w-full h-full object-contain" />
                  : <ClipboardList className="w-8 h-8 text-zinc-700" />}
              </div>
              <div className="text-sm text-zinc-200 truncate">{u.name}</div>
              <div className="text-xs text-[#D4AF37] mt-0.5">Qty: {u.quantity} {u.uom || "pcs"}</div>
              <div className="flex items-center justify-end gap-1 mt-2">
                <button onClick={(e) => { e.stopPropagation(); setEditing(u); }} data-testid={`utensil-edit-${u.id}`} className="p-1.5 text-zinc-500 hover:text-[#D4AF37]"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); remove(u); }} data-testid={`utensil-del-${u.id}`} className="p-1.5 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="col-span-full text-center text-sm text-zinc-600 py-16">
            {utensils.length === 0 ? "No utensils yet — click New Utensil to build your library." : `No utensils match "${q}"`}
          </div>
        )}
      </div>

      <UtensilFormDialog editing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <AssignDialog open={assignOpen} selected={selected} utensils={utensils} orders={orders}
        setSelected={setSelected}
        onClose={() => setAssignOpen(false)}
        onDone={() => { setAssignOpen(false); setSelected({}); }} />
    </div>
  );
}

function UtensilFormDialog({ editing, onClose, onSaved }) {
  const open = editing !== null;
  const isNew = open && !editing.id;
  const [f, setF] = useState({ name: "", quantity: 1, uom: "pcs", image_data_url: null });
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setF({ name: editing.name || "", quantity: editing.quantity || 1, uom: editing.uom || "pcs", image_data_url: editing.image_data_url || null });
  }, [open, editing]);

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setF((s) => ({ ...s, image_data_url: dataUrl }));
    } catch { toast.error("Could not read image"); }
    e.target.value = "";
  };

  const save = async () => {
    try {
      if (isNew) await api.post("/utensils", f);
      else await api.patch(`/utensils/${editing.id}`, f);
      toast.success(isNew ? "Utensil saved to library" : "Utensil updated");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-sm" data-testid="utensil-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isNew ? "New Utensil" : "Edit Utensil"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="utensil-pic-btn"
            className="w-full h-32 rounded-sm bg-[#101010] border border-dashed border-[#2a2a2a] hover:border-[#D4AF37] flex items-center justify-center overflow-hidden transition-colors">
            {f.image_data_url
              ? <img src={f.image_data_url} alt="utensil" className="w-full h-full object-contain" />
              : <span className="flex items-center gap-2 text-xs text-zinc-500"><ImagePlus className="w-4 h-4" /> Add picture</span>}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden" data-testid="utensil-pic-input" />
          <div>
            <Label className="label-eyebrow text-[10px]">Utensil Name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Chafing Dish (Full Size)" data-testid="utensil-name-input" className="bg-[#050505] border-[#2a2a2a] mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow text-[10px]">Quantity (default when assigned)</Label>
              <Input type="number" min="1" value={f.quantity} onChange={(e) => setF({ ...f, quantity: Number(e.target.value) })} data-testid="utensil-quantity-input" className="bg-[#050505] border-[#2a2a2a] mt-1" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">UOM</Label>
              <select value={f.uom} onChange={(e) => setF({ ...f, uom: e.target.value })} data-testid="utensil-uom-select" className="w-full mt-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm capitalize">
                {UTENSIL_UOMS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <Button onClick={save} disabled={!f.name.trim()} data-testid="utensil-save-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] w-full">{isNew ? "Save to Library" : "Save Changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ open, selected, setSelected, utensils, orders, onClose, onDone }) {
  const [orderId, setOrderId] = useState("");
  const [saving, setSaving] = useState(false);
  const chosen = utensils.filter((u) => selected[u.id] !== undefined);

  const assign = async () => {
    if (!orderId) { toast.error("Pick an order"); return; }
    setSaving(true);
    try {
      const r = await api.post("/utensils/assign", {
        order_id: orderId,
        items: chosen.map((u) => ({ utensil_id: u.id, qty: Number(selected[u.id]) || 1 })),
      });
      toast.success(`Assigned ${r.data.added} utensil(s) to the order${r.data.skipped ? ` (${r.data.skipped} already on it)` : ""}`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-md" data-testid="assign-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Assign to Order</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow text-[10px]">Designated order</Label>
            <select value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="assign-order-select" className="w-full mt-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
              <option value="">— Choose order —</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>{o.customer_name}{o.job_title ? ` · ${o.job_title}` : ""} · {new Date(o.event_date).toLocaleDateString("en-MY", { day: "numeric", month: "short" })}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {chosen.map((u) => (
              <div key={u.id} className="flex items-center gap-2 text-sm">
                <span className="flex-1 truncate text-zinc-300">{u.name}</span>
                <Input type="number" min="1" value={selected[u.id]} data-testid={`assign-qty-${u.id}`}
                  onChange={(e) => setSelected((s) => ({ ...s, [u.id]: e.target.value }))}
                  className="w-20 h-8 bg-[#050505] border-[#2a2a2a]" />
                <span className="text-xs text-zinc-500 w-10">{u.uom || "pcs"}</span>
              </div>
            ))}
          </div>
          <Button onClick={assign} disabled={saving || !orderId || chosen.length === 0} data-testid="assign-confirm-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] w-full">
            {saving ? "Assigning…" : `Assign ${chosen.length} utensil(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
